import { useEffect, useRef, useState } from 'react';
import { BudgetRequestListItemDto } from '../../../types';
import { useBrgList } from '../../../hooks/useBrgList';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { UserDropdownDto } from '../../../types';

const DENY_REASON_MAX_LENGTH = 500;

interface BudgetRequestReviewModalProps {
  request: BudgetRequestListItemDto;
  onClose: () => void;
  onApprove: () => void;
  onDeny: (denyReason: string) => void;
  isLoading?: boolean;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

export function BudgetRequestReviewModal({
  request,
  onClose,
  onApprove,
  onDeny,
  isLoading = false,
}: BudgetRequestReviewModalProps) {
  const { data: brgs = [] } = useBrgList();
  // Fetch all users (including inactive) to ensure we can resolve all requesters
  const { data: users = [] } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => apiClient.get<UserDropdownDto[]>('/api/users?active=false'),
  });
  
  const isPending = request.budgetStatusId === 1;

  // Deny flow: Admin must enter a Reason for Denial before the denial is finalized
  const [isDenying, setIsDenying] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [denyReasonError, setDenyReasonError] = useState<string | null>(null);
  const denyReasonRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isDenying) {
      denyReasonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      denyReasonRef.current?.focus();
    }
  }, [isDenying]);

  const handleDenyClick = () => {
    setDenyReasonError(null);
    setIsDenying(true);
  };

  const handleCancelDeny = () => {
    setIsDenying(false);
    setDenyReason('');
    setDenyReasonError(null);
  };

  const handleConfirmDeny = () => {
    const trimmed = denyReason.trim();
    if (!trimmed) {
      setDenyReasonError('Reason for Denial is required.');
      denyReasonRef.current?.focus();
      return;
    }
    onDeny(trimmed);
  };

  // Find BRG name from dynamic data
  const brgName = brgs.find(b => b.brgId === request.brgId)?.name || `BRG ${request.brgId}`;
  
  // Find user name from dynamic data
  const submittedBy = users.find(u => u.userId === request.requestedById)?.displayName || '—';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.45)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '560px',
          maxWidth: '95vw',
          maxHeight: '88vh',
          overflowY: 'auto',
          boxShadow: '0 8px 40px rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Header */}
        <div style={{
          backgroundColor: '#1a3bcc',
          color: 'white',
          padding: '16px 22px',
          borderRadius: '8px 8px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}>
          <h2 style={{
            fontSize: '15px',
            fontWeight: 800,
            color: 'white',
            margin: 0,
          }}>Budget Request Review</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '20px',
              cursor: 'pointer',
              lineHeight: 1,
              padding: '0 4px',
              fontWeight: 700,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '22px 26px',
        }}>
            {/* Submission Notice */}
            <div style={{
              backgroundColor: '#f0f4ff',
              borderLeft: '4px solid #1a3bcc',
              padding: '10px 14px',
              fontSize: '11px',
              color: '#333',
              marginBottom: '18px',
              borderRadius: '0 4px 4px 0',
            }}>
              <strong style={{ color: '#1a3bcc' }}>Submission Notice:</strong> Budget requests are reviewed by the BRG Admin. The lead will receive an email notification once their request has been approved or denied.
            </div>

            {/* BRG Information */}
            <div style={{
              fontSize: '10px',
              fontWeight: 800,
              color: '#888',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              borderBottom: '1px solid #eee',
              paddingBottom: '5px',
              marginBottom: '12px',
              marginTop: '16px',
            }}>
              BRG Information
            </div>

            <div style={{ marginBottom: '13px' }}>
              <label style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#444',
                marginBottom: '4px',
                display: 'block',
              }}>
                BRG Name <span style={{ color: '#cc1a1a' }}>*</span>
              </label>
              <div style={{
                backgroundColor: '#f8f9fb',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '8px 12px',
                fontSize: '12px',
                color: '#222',
                width: '100%',
              }}>
                {brgName}
              </div>
            </div>

            <div style={{ marginBottom: '13px' }}>
              <label style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#444',
                marginBottom: '4px',
                display: 'block',
              }}>
                Submitted By <span style={{ color: '#cc1a1a' }}>*</span>
              </label>
              <div style={{
                backgroundColor: '#f8f9fb',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '8px 12px',
                fontSize: '12px',
                color: '#222',
                width: '100%',
              }}>
                {submittedBy}
              </div>
            </div>

            {/* Request Details */}
            <div style={{
              fontSize: '10px',
              fontWeight: 800,
              color: '#888',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              borderBottom: '1px solid #eee',
              paddingBottom: '5px',
              marginBottom: '12px',
              marginTop: '16px',
            }}>
              Request Details
            </div>

            <div style={{ marginBottom: '13px' }}>
              <label style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#444',
                marginBottom: '4px',
                display: 'block',
              }}>
                Request Amount <span style={{ color: '#cc1a1a' }}>*</span>
              </label>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  backgroundColor: '#eef0f4',
                  border: '1px solid #ddd',
                  borderRight: 'none',
                  borderRadius: '4px 0 0 4px',
                  padding: '8px 10px',
                  fontSize: '12px',
                  color: '#555',
                  fontWeight: 700,
                }}>
                  $
                </div>
                <div style={{
                  backgroundColor: '#f8f9fb',
                  border: '1px solid #ddd',
                  borderRadius: '0 4px 4px 0',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#222',
                  flex: 1,
                }}>
                  {formatCurrency(request.requestAmount).replace('$', '')}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '13px' }}>
              <label style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#444',
                marginBottom: '4px',
                display: 'block',
              }}>
                Project / Event <span style={{ fontWeight: 400, color: '#999', fontSize: '10px' }}>(optional)</span>
              </label>
              <div style={{
                backgroundColor: '#f8f9fb',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '8px 12px',
                fontSize: '12px',
                color: '#222',
                width: '100%',
                minHeight: '40px',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
              }}>
                {request.projectEvent || ''}
              </div>
            </div>

            <div style={{ marginBottom: '13px' }}>
              <label style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#444',
                marginBottom: '4px',
                display: 'block',
              }}>
                Scheduled Date <span style={{ fontWeight: 400, color: '#999', fontSize: '10px' }}>(optional)</span>
              </label>
              <div style={{
                backgroundColor: '#f8f9fb',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '8px 12px',
                fontSize: '12px',
                color: '#222',
                width: '100%',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
              }}>
                {request.scheduledDate ? (() => {
                  const [year, month, day] = request.scheduledDate.split('-');
                  const m = String(parseInt(month)).padStart(2, '0');
                  const d = String(parseInt(day)).padStart(2, '0');
                  return `${m}/${d}/${year}`;
                })() : ''}
              </div>
            </div>

            <div style={{ marginBottom: '13px' }}>
              <label style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#444',
                marginBottom: '4px',
                display: 'block',
              }}>
                Purpose / Description <span style={{ color: '#cc1a1a' }}>*</span>
              </label>
              <div style={{
                backgroundColor: '#f8f9fb',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '8px 12px',
                fontSize: '12px',
                color: '#222',
                width: '100%',
              }}>
                {request.purposeDescription || ''}
              </div>
            </div>

            <div style={{ marginBottom: '13px' }}>
              <label style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#444',
                marginBottom: '4px',
                display: 'block',
              }}>
                Request Description <span style={{ color: '#cc1a1a' }}>*</span>
              </label>
              <div style={{
                backgroundColor: '#f8f9fb',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '8px 12px',
                fontSize: '12px',
                color: '#222',
                width: '100%',
                minHeight: '70px',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
              }}>
                {request.requestDescription || ''}
              </div>
            </div>

            {/* Requests auto-created from Project Tracking have no Impact */}
            {!request.projectId && (
              <div style={{ marginBottom: '13px' }}>
                <label style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}>
                  Impact <span style={{ color: '#cc1a1a' }}>*</span>
                </label>
                <div style={{
                  backgroundColor: '#f8f9fb',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#222',
                  width: '100%',
                  minHeight: '70px',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                }}>
                  {request.impact || ''}
                </div>
              </div>
            )}

            {request.budgetStatusId === 3 && (
              <div style={{ marginBottom: '13px' }}>
                <label style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}>
                  Reason for Denial
                </label>
                <div
                  data-testid="deny-reason-display"
                  style={{
                    backgroundColor: '#fdf3f4',
                    border: '1px solid #f1c2c6',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    fontSize: '12px',
                    color: '#222',
                    width: '100%',
                    minHeight: '40px',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                  }}
                >
                  {request.denyReason || '—'}
                </div>
              </div>
            )}

            {isPending && isDenying && (
              <div style={{ marginBottom: '13px' }}>
                <label
                  htmlFor="deny-reason"
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#444',
                    marginBottom: '4px',
                    display: 'block',
                  }}
                >
                  Reason for Denial <span style={{ color: '#cc1a1a' }}>*</span>
                </label>
                <textarea
                  id="deny-reason"
                  ref={denyReasonRef}
                  data-testid="deny-reason-input"
                  value={denyReason}
                  onChange={(e) => {
                    setDenyReason(e.target.value);
                    if (denyReasonError && e.target.value.trim()) setDenyReasonError(null);
                  }}
                  maxLength={DENY_REASON_MAX_LENGTH}
                  disabled={isLoading}
                  required
                  aria-required="true"
                  aria-invalid={!!denyReasonError}
                  aria-describedby={denyReasonError ? 'deny-reason-error' : undefined}
                  placeholder="Enter the reason this request is being denied"
                  style={{
                    border: `1px solid ${denyReasonError ? '#cc1a1a' : '#ddd'}`,
                    borderRadius: '4px',
                    padding: '8px 12px',
                    fontSize: '12px',
                    color: '#222',
                    width: '100%',
                    minHeight: '80px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  <span id="deny-reason-error" role="alert" style={{ fontSize: '11px', color: '#cc1a1a' }}>
                    {denyReasonError}
                  </span>
                  <span style={{ fontSize: '10px', color: '#999' }}>
                    {denyReason.length}/{DENY_REASON_MAX_LENGTH}
                  </span>
                </div>
              </div>
            )}
          </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          gap: '12px',
          padding: '16px 26px 22px',
          borderTop: '1px solid #eee',
          marginTop: '4px',
        }}>
          {isPending && isDenying ? (
            <>
              <button
                onClick={handleCancelDeny}
                disabled={isLoading}
                data-testid="cancel-deny-button"
                style={{
                  backgroundColor: 'white',
                  color: '#444',
                  border: '1px solid #ccc',
                  borderRadius: '5px',
                  padding: '10px 28px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  marginLeft: 'auto',
                  opacity: isLoading ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeny}
                disabled={isLoading}
                data-testid="confirm-deny-button"
                style={{
                  backgroundColor: isLoading ? '#a0c8b0' : '#a01a23',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  padding: '10px 28px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.6 : 1,
                }}
                onMouseOver={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = '#7e141b';
                }}
                onMouseOut={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = '#a01a23';
                }}
              >
                {isLoading ? 'Processing...' : 'Confirm Denial'}
              </button>
            </>
          ) : isPending ? (
            <>
              <button
                onClick={handleDenyClick}
                disabled={isLoading}
                data-testid="deny-button"
                style={{
                  backgroundColor: isLoading ? '#a0c8b0' : '#a01a23',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  padding: '10px 28px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  marginLeft: 'auto',
                  opacity: isLoading ? 0.6 : 1,
                }}
                onMouseOver={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = '#7e141b';
                }}
                onMouseOut={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = '#a01a23';
                }}
              >
                {isLoading ? 'Processing...' : 'Deny'}
              </button>
              <button
                onClick={onApprove}
                disabled={isLoading}
                data-testid="approve-button"
                style={{
                  backgroundColor: isLoading ? '#a0c8b0' : '#1a7c3a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  padding: '10px 28px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.6 : 1,
                }}
                onMouseOver={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = '#155f2d';
                }}
                onMouseOut={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = '#1a7c3a';
                }}
              >
                {isLoading ? 'Processing...' : 'Approve'}
              </button>
            </>
          ) : (
            <>
              <span style={{
                fontSize: '12px',
                fontWeight: 700,
                padding: '6px 14px',
                borderRadius: '5px',
                marginRight: 'auto',
                backgroundColor: request.budgetStatusId === 2 ? '#d4edda' : '#f8d7da',
                color: request.budgetStatusId === 2 ? '#1a7c3a' : '#a01a23',
              }}>
                ✓ This request has already been {request.budgetStatusId === 2 ? 'Approved' : 'Denied'}
              </span>
              <button
                disabled
                style={{
                  backgroundColor: '#a0c8b0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  padding: '10px 28px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'not-allowed',
                  opacity: 0.6,
                }}
              >
                Deny
              </button>
              <button
                disabled
                style={{
                  backgroundColor: '#a0c8b0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  padding: '10px 28px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'not-allowed',
                  opacity: 0.6,
                }}
              >
                Approve
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
